/**
 * The council: what the AI nations do with their turn.
 *
 * The policy is deliberately small enough to state in full, because a strategic
 * AI the player cannot predict is indistinguishable from one that is cheating.
 * Every nation, in index order, does the same four things:
 *
 *   1. **Muster** if it is rich enough and has fewer hosts than it is allowed.
 *   2. **Put to sea** — build a harbour where the water is worth most, keep
 *      hulls if the coast is long enough to be worth defending, and look for a
 *      landing. See the admiralty section below.
 *   3. **Defend** — if one of its own provinces is about to be lost, the
 *      nearest host that can reach it goes there. This outranks everything.
 *   4. **Take the free thing** — an undefended enemy or unclaimed province
 *      next to a host is walked into regardless of temperament. Nobody is too
 *      cautious to pick up something nobody is holding.
 *   5. **Attack** the softest worthwhile target where its local strength beats
 *      the defence by the margin its `caution` demands, at a rate its
 *      `aggression` sets.
 *
 * Nothing here calls `Math.random`. Every roll comes from `world.rngCampaign`,
 * which is seeded from the run seed, hashed, and saved — so a campaign replays.
 *
 * ── Why the admiralty lives in this file ─────────────────────────────────
 *
 * It could have been its own module, and was going to be. It is here because a
 * landing is scored with `provinceValue` over `targetDefence`, exactly as a
 * march is, and two copies of "what is that province worth and what does it
 * cost" would drift — which would show up as the AI valuing ground differently
 * depending on whether it planned to walk or sail there, for no reason anybody
 * could name. The same argument that put `rollBattle` in one place puts this
 * one here.
 */

import { nextFloat } from '../../core/rng.ts';
import { NATIONS, nationCapital, nationDef } from '../../data/nations.ts';
import { isCoastalProvince, provinceDef, provinceIndex } from '../../data/provinces.ts';
import type { World } from '../world.ts';
import { neighbours } from './graph.ts';
import { isIntruding, passageHops, passageStep, withdrawStep } from './diplopass.ts';
import { campAt } from './bandits.ts';
import { tradeWorthOf } from './trade.ts';
import {
  canInterceptFleet,
  disembarkFleet,
  embarkArmy,
  findFleet,
  fleetCapacity,
  fleetPoints,
  fleetPrice,
  fleetsOf,
  isSailing,
  landingRefusal,
  laneAdjacency,
  lanesFrom,
  maxAffordableShips,
  orderFleetIntercept,
  orderSail,
  portPrice,
  portRefusal,
  portsOf,
  tryAddHulls,
  tryBuildPort,
  tryRaiseFleet,
} from './fleets.ts';
import {
  armiesOf,
  armiesStandingIn,
  armyPoints,
  canMarch,
  orderMarch,
  rawPoints,
  tryRaiseArmy,
  tryReinforceArmy,
} from './armies.ts';
import {
  atWar,
  defenceMultiplier,
  garrisonCap,
  logLine,
  mayAttack,
  nationName,
  provinceName,
} from './state.ts';
import { tierOf, tierYield } from './holdings.ts';
import {
  ArmyStance,
  IMPATIENCE_FLOOR,
  IMPATIENCE_PER_TURN,
  MAX_ARMIES_PER_NATION,
  MAX_ARMY_POINTS,
  MAX_FLEETS_PER_NATION,
  MIN_ARMY_POINTS,
  MIN_FLEET_SHIPS,
  RAISE_GOLD_PER_POINT,
  SHIP_CARRY,
  type Army,
  type CampaignState,
  type Fleet,
} from './types.ts';

/**
 * How many provinces each crown is holding, counted once.
 *
 * `leaderBias` needs this, and used to recount the whole map on every call —
 * inside the per-candidate loop of both `attack` and `campaignFor`, so once per
 * host per candidate province. That is provinces × provinces × hosts of reading
 * for a number that cannot change while the council is sitting: nothing in
 * `councilTurn` captures ground. Orders are given here and carried out in next
 * month's `March` and `Battles` phases, which is why hoisting it to the top of
 * the sitting is exact rather than approximate — the same integers, computed
 * once instead of sixteen thousand times.
 *
 * `doIncome` builds the same array for the same reason. If a third caller
 * appears, that is the moment this becomes a field rather than a local.
 */
function holdingsOf(c: CampaignState): number[] {
  const holdings = c.nations.map(() => 0);
  for (const prov of c.provinces) if (prov.owner >= 0) holdings[prov.owner]++;
  return holdings;
}

export function councilTurn(w: World): void {
  const c = w.campaign;
  if (!c || c.over) return;
  const holdings = holdingsOf(c);
  for (let n = 0; n < c.nations.length; n++) {
    // Every human crown steers itself. None of them is the AI's to play.
    if (c.humanNations.includes(n)) continue;
    if (!c.nations[n].alive) continue;
    nationTurn(w, n, holdings);
  }
}

function nationTurn(w: World, n: number, holdings: number[]): void {
  const c = w.campaign;
  if (!c) return;
  const def = nationDef(c.nations[n].def);

  /**
   * The admiralty sits before the muster, and the order is the whole reason
   * anybody has a navy.
   *
   * `muster` spends `treasury.gold × militarism` every single month. Run first,
   * it takes its cut off the top before anything else is considered, and a
   * harbour costing three hundred gold is simply never reached — the purse
   * never holds three hundred gold for a whole month. Measured: two ports and
   * four hulls across thirty crowns in a hundred and twenty months, no landing
   * ever attempted, the entire sea unused.
   *
   * A harbour is capital and an army is wages, and capital is what you commit
   * before you find out what is left. The gates in `buildHarbour` and
   * `layDownHulls` are what stop that becoming "spend everything on ships":
   * both keep three times the bill back, so a crown builds when it is genuinely
   * flush and musters with the rest.
   */
  const plan = admiralty(w, n);
  muster(w, n);

  // Armies act in id order, oldest first, so the veteran host gets first refusal
  // on the important job. A host already aboard a ship is not on the board and
  // takes no orders here: what happens to it is what happens to the fleet.
  const hosts = armiesOf(c, n)
    .filter((a) => a.moveTo < 0 && a.aboard < 0 && a.regiments.length > 0)
    .sort((x, y) => x.id - y.id);

  const spokenFor = new Set<number>();
  let boarded = false;
  for (const a of hosts) {
    // Above everything, including defence of the homeland: a host standing on
    // ground it has no right to be on has one job, and every other clause in
    // this loop would fail for it anyway. See `withdraw`.
    if (withdraw(c, n, a)) continue;
    if (defend(c, n, a, spokenFor)) continue;
    if (police(c, n, a, spokenFor)) continue;
    // The expedition, if there is one, and only ever one host at a time — a
    // crown that put every army it had onto ships would have no country left
    // behind them, and losing one sea battle would end it.
    if (!boarded && board(c, n, a, plan)) {
      boarded = true;
      continue;
    }
    if (seize(c, n, a, spokenFor)) continue;
    if (attack(w, n, a, def.aggression, def.caution, spokenFor, holdings)) continue;
    // Nothing next door: go and find a war rather than pacing your own border.
    if (campaignFor(c, n, a, def.caution, holdings)) continue;
    consolidate(c, n, a);
    // Nothing was found for it this turn. A host that keeps being told to wait
    // gets less particular about what it will settle for.
    a.idleTurns = Math.min(60, a.idleTurns + 1);
  }
}

// ── 1. Muster ──────────────────────────────────────────────────────────────

function muster(w: World, n: number): void {
  const c = w.campaign;
  if (!c) return;
  const nation = c.nations[n];
  const def = nationDef(nation.def);
  const hosts = armiesOf(c, n);

  // Spend the share of the purse its militarism says it will.
  const points = Math.round((nation.treasury.gold * def.militarism) / RAISE_GOLD_PER_POINT);
  if (points < MIN_ARMY_POINTS * 0.4) return;

  if (hosts.length >= MAX_ARMIES_PER_NATION) {
    reinforceWeakest(w, n, points);
    return;
  }
  if (points < MIN_ARMY_POINTS) return;
  // A host that has been mauled is worth mending before a new one is raised —
  // but only a mauled one. Preferring reinforcement unconditionally, which this
  // did first, produces nations that field exactly one enormous army and can
  // therefore only ever be in one place.
  const mauled = hosts.some(
    (a) => a.moveTo < 0 && c.provinces[a.at].owner === n && rawPoints(a) < MAX_ARMY_POINTS * 0.45,
  );
  if (mauled && reinforceWeakest(w, n, points * 0.6) > 0) return;
  // Otherwise it waits until it can put a real army in the field, not a screen.
  if (hosts.length > 0 && points < MIN_ARMY_POINTS * 2) return;

  const at = musterSite(c, n);
  if (at < 0) return;
  const raised = tryRaiseArmy(c, w.rngCampaign, n, at, points);
  if (raised) logLine(c, `${nationName(c, n)} raises the ${raised.name}.`);
}

/** Top up the most beaten-up host sitting on friendly ground. */
function reinforceWeakest(w: World, n: number, points: number): number {
  const c = w.campaign;
  if (!c) return 0;
  let worst: Army | undefined;
  let worstPoints = Infinity;
  for (const a of armiesOf(c, n)) {
    if (a.moveTo >= 0) continue;
    if (c.provinces[a.at].owner !== n) continue;
    const strength = rawPoints(a);
    if (strength < worstPoints) {
      worstPoints = strength;
      worst = a;
    }
  }
  if (!worst) return 0;
  const models = tryReinforceArmy(c, w.rngCampaign, n, worst.id, points);
  /**
   * The player's own levies only.
   *
   * Every crown tops up a host most months, so on a board of thirty this was
   * the single most frequent line in the chronicle and the least informative:
   * "fourteen more march to join the Second Host of Halethwaite" tells the
   * player nothing they can act on about a nation they may never meet. It
   * pushed sieges and battles off the end of a forty-eight line log inside two
   * months. What a hostile crown is *doing* reaches the player through
   * `refreshWarnings`, which says it only when it is coming for them.
   */
  if (models > 0 && n === c.playerNation) {
    logLine(c, `${models} more march to join the ${worst.name}.`);
  }
  return models;
}

/** Raise where it is safe: the capital if it is still held, otherwise deep ground. */
function musterSite(c: CampaignState, n: number): number {
  const capital = provinceIndex(nationDef(c.nations[n].def).capital);
  if (capital >= 0 && c.provinces[capital].owner === n) return capital;
  let best = -1;
  let bestScore = -Infinity;
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== n) continue;
    const exposed = neighbours(c, p).filter((q) => c.provinces[q].owner !== n).length;
    const score = -exposed * 10 + c.provinces[p].fort * 3;
    if (score > bestScore) {
      bestScore = score;
      best = p;
    }
  }
  return best;
}

// ── 1b. The admiralty ──────────────────────────────────────────────────────

/**
 * A landing this crown could make: which fleet, out of which harbour, at what.
 *
 * Computed once per nation per month and then read by both halves of the
 * council — the fleet half loads and sails, the host half walks a host to the
 * quay. Keeping it one object is what stops the two disagreeing about which
 * expedition is being mounted, which is the failure mode a pair of independent
 * heuristics has.
 */
interface NavalPlan {
  fleetId: number;
  /** The harbour the expedition sails from. Held, ported, unbesieged. */
  port: number;
  /** The coast it is for. One lane out. */
  target: number;
  months: number;
}

/**
 * How much of this crown is coast, 0..1 — and therefore how much it cares.
 *
 * The whole naval temperament, and it is read off the map rather than written
 * into `nations.ts` on purpose. A crown that generation happened to seat on a
 * peninsula *becomes* a sea power because its ground is coast, and one seated
 * in the interior never builds a hull however aggressive it is. That is the
 * brief's point about geography being strategy, applied to the AI's character
 * rather than only to its options: the map decides who the naval powers are,
 * and it decides differently on a different seed.
 */
function navalAppetite(c: CampaignState, n: number): number {
  let held = 0;
  let coast = 0;
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== n) continue;
    held++;
    if (isCoastalProvince(p)) coast++;
  }
  if (held === 0) return 0;
  return coast / held;
}

/**
 * The naval half of a nation's turn: harbours, hulls, and where to point them.
 *
 * Returns the expedition it wants a host for, or null. The cheap exit at the
 * top matters more than anything else in here — most crowns on most turns hold
 * no coast at all, and they should pay one loop for finding that out rather
 * than a survey of the sea.
 */
function admiralty(w: World, n: number): NavalPlan | null {
  const c = w.campaign;
  if (!c) return null;

  const appetite = navalAppetite(c, n);
  const fleets = fleetsOf(c, n).sort((x, y) => x.id - y.id);
  if (appetite <= 0 && fleets.length === 0) return null;

  buildHarbour(c, n, appetite);

  /**
   * Anything that can catch a convoy does, and is then unavailable for
   * anything else. Sinking transports outranks mounting an invasion.
   *
   * The precedence is not a preference, it is the arithmetic. A hostile host at
   * sea is the one moment in its whole existence when it can be destroyed
   * outright rather than beaten and allowed to retreat — and it costs a fleet
   * one month to try. Nothing else a navy can do in a month is worth as much.
   *
   * It also had to be stated explicitly, because without it the AI never
   * intercepted anything at all. `bestLanding` assigns every berthed fleet it
   * can to an expedition, and a crown that loads its only fleet has nothing
   * left to chase with — thirty fleet-months of loaded convoys crossing open
   * water and not one attempt on any of them.
   */
  const hunting = new Set<number>();
  for (const f of fleets) {
    if (isSailing(f) || f.cargo.length > 0) continue;
    if (hunt(c, f)) hunting.add(f.id);
  }

  const plan = bestLanding(c, n, fleets.filter((f) => !hunting.has(f.id)));

  // Fleets act in id order, like hosts, and for the same reason.
  for (const f of fleets) {
    if (isSailing(f) || hunting.has(f.id) || f.cargo.length === 0) continue;

    // Carrying somebody. Either this is the beach, or it is time to sail, or
    // the expedition is off.
    if (landingRefusal(c, f) === '' && c.provinces[f.at].owner !== n) {
      const landed = disembarkFleet(c, f);
      if (landed > 0) {
        logLine(c, `${nationName(c, n)} comes ashore at ${provinceName(f.at)} off ${f.name}.`);
      }
      continue;
    }
    /**
     * A loaded fleet reads its *own* best target rather than the crown's.
     *
     * It used to sail only if the single best expedition on the board happened
     * to name it, which stranded armies. `bestLanding` is recomputed every
     * month over every berthed fleet, so on the month after a host went aboard
     * the winner could easily be a different fleet — and this one would sit in
     * harbour holding an entire army out of the land war. Measured: seventy-
     * eight host-months at sea against two fleet-months of actual sailing.
     * The hosts were not at sea. They were in a boat, at the quay, for the rest
     * of the campaign.
     *
     * So a fleet with somebody aboard asks the question for itself, and if the
     * answer is that there is nothing worth crossing for, it puts them back
     * ashore. Committing an army is reversible; losing one to a scheduling
     * accident is not.
     */
    const own = bestTargetFor(c, n, f);
    if (own >= 0) {
      orderSail(c, f, own);
      continue;
    }
    if (disembarkFleet(c, f) > 0) {
      continue;
    }
    /**
     * It cannot land here and has nowhere worth crossing to. Go home.
     *
     * The case that makes this necessary: a convoy sails for an enemy coast, a
     * peace is signed while it is at sea, and it arrives somewhere it is no
     * longer allowed to put anybody ashore. Without a way back it would sit
     * off a foreign beach with an army aboard until the campaign ended — which
     * is precisely what it did.
     */
    for (const lane of lanesFrom(c, f.at)) {
      const q = lane.a === f.at ? lane.b : lane.a;
      if (c.provinces[q].owner !== n) continue;
      if (!orderSail(c, f, q)) continue;
      break;
    }
  }

  layDownHulls(c, n, appetite);
  return plan;
}

/**
 * Raise or extend the harbour where the water is worth most.
 *
 * The score is lanes times wealth, which is `portIncome`'s own arithmetic read
 * forward: a harbour earns per partner it can reach, so the province with the
 * most lanes out of it is the one that will earn most once anybody else on
 * those lanes builds too. It converges on the natural hubs — the mouths of the
 * Sundered Sea and the two great peninsulas — without anything having been told
 * where they are.
 *
 * The treasury gate is deliberately steep. A harbour that beggars the crown
 * that built it is a worse decision than no harbour, and the AI has to make the
 * same trade the player does.
 */
function buildHarbour(c: CampaignState, n: number, appetite: number): void {
  const nation = c.nations[n];
  if (appetite < 0.2) return;
  const adj = laneAdjacency(c);

  let best = -1;
  let bestScore = 0;
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== n) continue;
    if (portRefusal(c, n, p) !== '') continue;
    const price = portPrice(c.provinces[p].port);
    // Keep three times the bill back. Ships and hosts still have to be paid for.
    if (nation.treasury.gold < price.gold * 3) continue;
    const score = adj[p].length * (1 + provinceDef(p).wealth / 12);
    if (score > bestScore) {
      bestScore = score;
      best = p;
    }
  }
  if (best < 0) return;
  // Unlogged, like everything else the admiralty does. See `layDownHulls`.
  tryBuildPort(c, n, best);
}

/**
 * Lay down hulls, if there is a harbour to lay them in and a reason to.
 *
 * Enough hulls to lift the largest host in the game, or as many as the purse
 * runs to — a fleet that cannot carry an army is only useful for meeting other
 * fleets, and a crown that has not decided to be a naval power should not be
 * spending its war chest on that.
 */
function layDownHulls(c: CampaignState, n: number, appetite: number): void {
  const nation = c.nations[n];
  if (fleetsOf(c, n).length >= MAX_FLEETS_PER_NATION) return;
  if (appetite < 0.25) return;
  const ports = portsOf(c, n).filter((p) => c.provinces[p].siegeBy < 0);
  if (ports.length === 0) return;

  // A navy is for using. Build one when there is somebody to use it on, or when
  // this crown is coastal enough that the sea is its border and its road both.
  const atWarWithSomebody = c.nations.some((_, m) => m !== n && atWar(c, n, m));
  if (!atWarWithSomebody && appetite < 0.6) return;

  /**
   * Half of what the purse could stretch to, capped at enough to lift a host.
   *
   * The reserve is expressed as a *share of what it could afford* rather than
   * as a multiple of the bill, and the difference is not cosmetic. The first
   * version asked for the largest fleet affordable and then refused to build it
   * unless the treasury held three times its price — which is a condition that
   * can never be met, since the fleet was sized by the treasury in the first
   * place. It built only when timber was the binding constraint and gold was
   * not, so every crown on the board floated the two-hull minimum and nothing
   * larger, forever.
   */
  const want = Math.min(Math.floor(maxAffordableShips(c, n) / 2), HULLS_FOR_A_HOST);
  if (want < 1) return;
  if (nation.treasury.gold < fleetPrice(want).gold * 2) return;

  /**
   * Grow a squadron before floating another, and the order matters more here
   * than it does for armies.
   *
   * `muster` prefers a new host over mending an old one unless the old one is
   * actually mauled, because a nation with one enormous army can only be in one
   * place. A navy is the opposite: a fleet's whole job is to be strong enough
   * to lift a host and to survive meeting somebody, and neither is divisible.
   * Three two-hull squadrons cannot do between them what one six-hull fleet
   * does alone — they cannot carry an army, and any one of them loses to it.
   */
  const mine = fleetsOf(c, n)
    .filter((f) => !isSailing(f) && f.ships < HULLS_FOR_A_HOST)
    .sort((x, y) => x.ships - y.ships || x.id - y.id);
  if (mine.length > 0) {
    if (tryAddHulls(c, n, mine[0].id, want) > 0) return;
  }
  if (want < MIN_FLEET_SHIPS) return;

  // Build in the busiest harbour: the most lanes out is the most places to go.
  const adj = laneAdjacency(c);
  let best = ports[0];
  for (const p of ports) if (adj[p].length > adj[best].length) best = p;

  /**
   * Nothing is written to the chronicle here, nor anywhere else in the
   * admiralty except a landing.
   *
   * The chronicle is `MAX_LOG` — forty-eight lines — and it is the player's,
   * not a transcript of the realm. Thirty crowns each opening a harbour,
   * laying down hulls, loading a host, standing out, putting about and coming
   * home is six lines a month of somebody else's shipping news, which pushed
   * every siege and every battle off the end of the log inside two months.
   * `tests/campaign.test.ts` caught it by asserting that a fight is still
   * legible after sixty turns, and it was right to.
   *
   * What survives is what changes the player's map: a landing, and a fight at
   * sea. Everything the player's own crown does is logged by `commands.ts`,
   * where it belongs — those are their orders and they asked for them.
   */
  tryRaiseFleet(c, n, best, want);
}

/** Hulls it takes to lift the largest host the raise command will build. */
const HULLS_FOR_A_HOST = Math.ceil(MAX_ARMY_POINTS / SHIP_CARRY);

/**
 * The best landing available to this crown, or null.
 *
 * Scored with the same `provinceValue` over `targetDefence` a march is, so a
 * fat capital across the water and a fat capital down the road are worth the
 * same thing and the AI picks between them on distance alone. What makes it a
 * *naval* decision is the gate: the sea has to be genuinely better than the
 * road. A coast three lanes away that is also four provinces away by land is
 * not worth a fleet, and an AI that sailed there anyway would look like it was
 * using its ships because it had them.
 */
function bestLanding(c: CampaignState, n: number, fleets: Fleet[]): NavalPlan | null {
  /**
   * Berthed, loaded or not — and the "or not" was a real bug rather than a
   * nicety.
   *
   * This filtered for `cargo.length === 0`, on the reasoning that a plan is
   * something you make before you load. But `board` loads a host in the *same*
   * month's host loop, one step after this runs, so by the following month the
   * fleet had cargo, was excluded from the survey, was named by no plan, and
   * sat in harbour forever. Every crown that built a navy loaded it and then
   * never sailed it anywhere: twenty-two harbours, thirty-two fleets, and not
   * one expedition in a hundred and twenty months.
   */
  /**
   * Empty, berthed, and in a harbour of this crown's own — because this is the
   * survey that decides where to *load*, and loading is the one thing that
   * genuinely needs a port.
   *
   * The port test belongs here and nowhere else. It used to sit inside the
   * shared survey, which marooned every fleet that ever arrived anywhere: a
   * convoy that made an enemy coast, found peace had been signed under it and
   * could not land, then asked where else to sail and was told "nowhere",
   * because the coast it was sitting off was not one of its own harbours. It
   * sat there with an army aboard for the rest of the campaign.
   */
  const berthed = fleets.filter(
    (f) =>
      !isSailing(f) &&
      f.ships > 0 &&
      f.cargo.length === 0 &&
      c.provinces[f.at].owner === n &&
      c.provinces[f.at].port > 0 &&
      c.provinces[f.at].siegeBy < 0,
  );
  if (berthed.length === 0) return null;

  let best: NavalPlan | null = null;
  let bestValue = 0;
  for (const f of berthed) {
    const scored = surveyFrom(c, n, f);
    if (!scored) continue;
    if (scored.value > bestValue) {
      bestValue = scored.value;
      best = { fleetId: f.id, port: f.at, target: scored.target, months: scored.months };
    }
  }
  return best;
}

/** The one coast this fleet would sail for, or -1. */
function bestTargetFor(c: CampaignState, n: number, f: Fleet): number {
  return surveyFrom(c, n, f)?.target ?? -1;
}

/**
 * The best coast one lane from where this fleet lies, scored, or null.
 *
 * Shared by the crown-wide survey and by a loaded fleet asking for itself, so
 * the two can never disagree about what is worth crossing for.
 */
function surveyFrom(
  c: CampaignState,
  n: number,
  f: Fleet,
): { target: number; months: number; value: number } | null {
  // Wherever it happens to be lying. A fleet needs a harbour to be built in and
  // to load at, and nothing at all to sail from — see `landingRefusal`.
  const port = f.at;
  const byLand = passageHops(c, n, port);
  const lift = fleetCapacity(f);

  let target = -1;
  let months = 0;
  let bestValue = 0;
  for (const lane of lanesFrom(c, port)) {
    const q = lane.a === port ? lane.b : lane.a;
    if (!mayAttack(c, n, q)) continue;
    /**
     * The sea must actually be the better road.
     *
     * Unreachable on foot is the clearest case and the one the isles exist
     * for. Otherwise the crossing has to save real walking, and the two sides
     * are compared in months against hops — which is not a unit error but a
     * deliberately conservative one. Every road link costs *at least* one
     * month (`LINK_MARCH` runs one to three), so hop count is a lower bound on
     * the march, and a gate that clears it would clear the true comparison by
     * more. The AI therefore never sails when walking was quicker, though it
     * occasionally walks when sailing would have been.
     */
    const land = byLand[q];
    if (land >= 0 && land < lane.months + SEA_WORTH_IT) continue;

    const cost = targetDefence(c, n, q);
    // A landing is unsupported by definition: no garrison of ours comes with
    // it and no second host is one province behind. It wants a real margin.
    if (lift * LANDING_MARGIN < cost) continue;
    // `n` and not a bare two-argument call: `provinceValue` learned about trade
    // while the sea was being built in a parallel worktree, and ground is worth
    // more to a crown that already controls the market it trades through. A
    // landing is exactly the decision that should feel that — taking a port on
    // a node you hold is worth more than taking one you have no factor in.
    const value = (provinceValue(c, q, n) * 40) / (Math.max(1, cost) * (1 + lane.months * 0.5));
    if (value > bestValue) {
      bestValue = value;
      target = q;
      months = lane.months;
    }
  }
  return target < 0 ? null : { target, months, value: bestValue };
}

/** Hops of road a crossing must save before the AI thinks it is worth ships. */
const SEA_WORTH_IT = 2;
/**
 * How much more than the defence a landing wants, measured in lift.
 *
 * Against the fleet's *capacity* rather than the host it happens to be carrying,
 * because this is decided before anybody is aboard. It is a coarse test and it
 * is meant to be: its job is to stop a crown sailing two hulls at a walled
 * capital, not to fine-tune an invasion.
 */
const LANDING_MARGIN = 1.3;

/**
 * Put a host aboard, or walk it to the quay.
 *
 * The second half is what makes any of this happen. An expedition that only
 * sailed when a host was already standing in the right harbour would mount
 * about once a campaign, because nothing else in the council has any reason to
 * send an army to a port.
 */
function board(c: CampaignState, n: number, a: Army, plan: NavalPlan | null): boolean {
  if (!plan) return false;
  const f = findFleet(c, plan.fleetId);
  if (!f || f.cargo.length > 0) return false;
  // Never the last host. A crown with one army does not put it on a boat.
  if (armiesOf(c, n).filter((x) => x.regiments.length > 0).length < 2) return false;
  if (rawPoints(a) > fleetCapacity(f)) return false;

  if (a.at === plan.port) {
    if (!embarkArmy(c, f, a)) return false;
    return true;
  }
  const next = stepToward(c, n, a.at, plan.port);
  if (next < 0 || !canMarch(c, a, next)) return false;
  orderMarch(c, a, next);
  return true;
}

/**
 * Send an empty war fleet after somebody's convoy.
 *
 * Loaded quarry only. An escort with nothing aboard takes no ground and
 * threatens nothing this month, so running one down buys a month of a fleet's
 * time for an advantage that may never be cashed — whereas the transports are
 * an entire army that can be deleted rather than merely defeated. A navy that
 * chased warships while an invasion sailed past would be doing the one thing a
 * navy exists to prevent.
 *
 * The fattest convoy it can beat, and it will not go out against something
 * clearly stronger: a fleet thrown away on a hopeless interception is a fleet
 * the next convoy sails past unopposed.
 */
function hunt(c: CampaignState, f: Fleet): boolean {
  let best: Fleet | undefined;
  let bestValue = 0;
  for (const other of c.fleets) {
    if (other.cargo.length === 0) continue;
    if (!canInterceptFleet(c, f, other)) continue;
    if (fleetPoints(other) > fleetPoints(f) * 1.1) continue;
    const value = other.ships + other.cargo.length * 4;
    if (value > bestValue) {
      bestValue = value;
      best = other;
    }
  }
  if (!best) return false;
  if (!orderFleetIntercept(c, f, best)) return false;
  return true;
}

// ── 1c. Get off somebody's land ────────────────────────────────────────────

/**
 * A host caught standing where it may no longer be, walking out.
 *
 * The most important six lines in this file for the border system, and it had to
 * be its own clause rather than falling out of the existing ones. Every other
 * order below is planned with `passageHops`, which walks only ground the crown
 * may enter — and the origin of a stranded host is, by definition, ground it may
 * not. So every distance it computes from there comes back unreachable, every
 * clause returns false, and the host stands in a foreign field until it starves.
 * Which is precisely the absurd result an AI that "respects borders" produces if
 * nobody writes down what respecting them looks like from the wrong side of one.
 *
 * `withdrawStep` walks the other way — outward from every province the crown
 * *may* stand on — so it always finds the shortest way home if there is one, and
 * `canMarch`'s withdrawal clause is what makes the step legal despite the ground
 * between here and there being just as closed.
 *
 * It can still fail, and honestly: a host put ashore on an island that belongs
 * entirely to a crown which then closes its border has nowhere on that island to
 * walk to. It needs hulls, and its crown either has them or does not.
 */
function withdraw(c: CampaignState, n: number, a: Army): boolean {
  if (!isIntruding(c, a)) return false;
  const next = withdrawStep(c, n, a.at);
  if (next < 0 || !canMarch(c, a, next)) return false;
  a.objective = -1;
  orderMarch(c, a, next);
  return true;
}

// ── 2. Defend ──────────────────────────────────────────────────────────────

/**
 * Threat is enemy strength standing on or beside a province, minus what is
 * already holding it. A walled province with a full garrison next to one enemy
 * host is therefore not "threatened", and does not pull a field army out of
 * position to babysit a wall that is doing its job.
 */
function defend(c: CampaignState, n: number, a: Army, spokenFor: Set<number>): boolean {
  let worst = -1;
  let worstDeficit = 0;

  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== n) continue;
    if (spokenFor.has(p)) continue;
    const threat = threatTo(c, n, p);
    if (threat <= 0) continue;
    const deficit = threat - holdingStrength(c, n, p);
    if (deficit > worstDeficit) {
      worstDeficit = deficit;
      worst = p;
    }
  }
  if (worst < 0) return false;

  if (a.at === worst) {
    // Already standing where the trouble is. Staying put *is* the order.
    spokenFor.add(worst);
    if (a.stance !== ArmyStance.Besieging) a.stance = ArmyStance.Field;
    return true;
  }
  if (canMarch(c, a, worst)) {
    orderMarch(c, a, worst);
    spokenFor.add(worst);
    return true;
  }
  const next = stepToward(c, n, a.at, worst);
  if (next >= 0 && canMarch(c, a, next)) {
    orderMarch(c, a, next);
    spokenFor.add(worst);
    return true;
  }
  return false;
}

function threatTo(c: CampaignState, n: number, p: number): number {
  let threat = 0;
  for (const q of [p, ...neighbours(c, p)]) {
    for (const a of armiesStandingIn(c, q)) {
      if (a.owner === n) continue;
      if (!atWar(c, n, a.owner)) continue;
      threat += armyPoints(a) * (q === p ? 1 : 0.7);
    }
  }
  return threat;
}

function holdingStrength(c: CampaignState, n: number, p: number): number {
  let held = c.provinces[p].garrison * defenceMultiplier(c, p);
  for (const a of armiesStandingIn(c, p)) if (a.owner === n) held += armyPoints(a);
  return held;
}

// ── 2b. Police your own back country ───────────────────────────────────────

/**
 * Go and burn out a camp on ground this nation holds.
 *
 * Above `seize` and below `defend`, which is the right place for it: brigands
 * are not an invasion, but a camp left standing chokes the province it sits on
 * every single turn and eventually puts another camp next door. A nation that
 * expands faster than it polices ends up with a back country full of them, which
 * is exactly the check on a runaway empire the map was missing.
 *
 * Only a host that clearly outmatches the camp goes; a nation feeding hosts into
 * brigands one at a time is worse for it than the brigands are.
 */
function police(c: CampaignState, n: number, a: Army, spokenFor: Set<number>): boolean {
  const mine = armyPoints(a);
  let best = -1;
  let bestValue = 0;
  for (const camp of c.camps) {
    if (spokenFor.has(camp.at)) continue;
    if (c.provinces[camp.at].owner !== n) continue;
    const cost = camp.strength * defenceMultiplier(c, camp.at);
    if (mine < cost * 1.35) continue;
    const value = provinceDef(camp.at).wealth / Math.max(1, passageHops(c, n, a.at)[camp.at] + 1);
    if (value > bestValue) {
      bestValue = value;
      best = camp.at;
    }
  }
  if (best < 0) return false;
  if (a.at === best) {
    // Standing on it already: the fight happens in the battle phase whether or
    // not anybody orders it, so the order is to stay.
    spokenFor.add(best);
    a.stance = ArmyStance.Field;
    return true;
  }
  const next = stepToward(c, n, a.at, best);
  if (next < 0 || !canMarch(c, a, next)) return false;
  orderMarch(c, a, next);
  spokenFor.add(best);
  return true;
}

// ── 3. Take the free thing ─────────────────────────────────────────────────

function seize(c: CampaignState, n: number, a: Army, spokenFor: Set<number>): boolean {
  for (const p of neighbours(c, a.at)) {
    if (spokenFor.has(p)) continue;
    if (!mayAttack(c, n, p)) continue;
    const prov = c.provinces[p];
    if (prov.fort > 0 || prov.garrison > 5) continue;
    // Ground with a camp on it is not free ground. `police` is where camps are
    // decided about, with the strength check that decision needs.
    if (campAt(c, p)) continue;
    if (armiesStandingIn(c, p).some((o) => o.owner !== n && atWar(c, n, o.owner))) continue;
    if (!canMarch(c, a, p)) continue;
    orderMarch(c, a, p);
    spokenFor.add(p);
    return true;
  }
  return false;
}

// ── 4. Attack ──────────────────────────────────────────────────────────────

function attack(
  w: World,
  n: number,
  a: Army,
  aggression: number,
  caution: number,
  spokenFor: Set<number>,
  holdings: number[],
): boolean {
  const c = w.campaign;
  if (!c) return false;

  // A host already sitting on a siege stays sitting on it. Walking away from a
  // half-starved garrison to look for a better target is how AI armies end up
  // pacing between two provinces for the rest of the campaign.
  if (a.stance === ArmyStance.Besieging && c.provinces[a.at].owner !== n) return true;

  if (nextFloat(w.rngCampaign) > 0.35 + aggression * 0.65) return false;

  const mine = armyPoints(a);
  /**
   * Cautious nations want most of two to one; reckless ones will take evens.
   *
   * Impatience is deliberately NOT applied here, though it was tried: letting a
   * bored host lower its bar for the province next door recovers some pressure
   * (3.46 to 3.88 hosts sent at the player) and doubles the runaway rate (2 of
   * 48 to 4 of 48), because the cheap local fight is exactly how a leader
   * compounds. It belongs on the long march, where the risk it is trading
   * against is real.
   */
  const wanted = 1 + caution * 1.1;

  let best = -1;
  let bestValue = 0;
  for (const p of neighbours(c, a.at)) {
    if (spokenFor.has(p)) continue;
    if (!mayAttack(c, n, p)) continue;
    if (!canMarch(c, a, p)) continue;
    const cost = targetDefence(c, n, p);
    if (mine < cost * wanted) continue;
    // Prefer the softest thing that is also worth something, and lean toward
    // whoever is currently winning. Without this the realm politely ignores a
    // runaway empire and takes turns eating whoever is already weakest, which
    // is both bad play and a boring campaign.
    const value = (provinceValue(c, p, n) * 40 * leaderBias(holdings, n, c.provinces[p].owner)) /
      Math.max(1, cost);
    if (value > bestValue) {
      bestValue = value;
      best = p;
    }
  }
  if (best < 0) return false;
  orderMarch(c, a, best);
  spokenFor.add(best);
  logLine(c, `${a.name} marches on ${provinceName(best)}.`);
  return true;
}

/**
 * Up to twice as attractive if the owner is six provinces ahead of you.
 *
 * Reads the counts rather than taking them: see `holdingsOf`. The arithmetic is
 * unchanged and is integer, so this is the same number it always was.
 */
function leaderBias(holdings: number[], me: number, owner: number): number {
  if (owner < 0) return 1;
  const ahead = Math.max(0, Math.min(6, (holdings[owner] ?? 0) - (holdings[me] ?? 0)));
  return 1 + ahead / 6;
}

function targetDefence(c: CampaignState, n: number, p: number): number {
  let def = c.provinces[p].garrison;
  for (const a of armiesStandingIn(c, p)) {
    if (a.owner !== n && atWar(c, n, a.owner)) def += armyPoints(a);
  }
  // Brigands are somebody standing on the ground with weapons, and an AI that
  // cannot see them walks small hosts into camps and loses them one at a time.
  const camp = campAt(c, p);
  if (camp) def += camp.strength;
  return Math.max(1, def * defenceMultiplier(c, p));
}

/**
 * What a province is worth holding: what it can garrison, plus what it pays.
 *
 * Takes the crown asking, because since trade landed the answer is no longer the
 * same for everybody. A third of a province's wealth is now trade, and trade is
 * paid out through control of the market it flows through — so the same ground
 * is worth more to a crown that already holds that market than to one that has
 * never traded through it. That is what makes a realm consolidate around a node
 * rather than eat in a straight line. See `tradeWorthOf`.
 */
function provinceValue(c: CampaignState, p: number, nation: number): number {
  const base =
    garrisonCap(c, p) / 60 +
    c.provinces[p].fort +
    provinceDef(p).wealth / 20 +
    tradeWorthOf(c, nation, p) / 20;
  /**
   * Development is what a province has been *made* into, and it has to be in
   * here or the realm cannot see the player at all.
   *
   * The player holds one province and pours hours into the city standing on it.
   * Left out, that city is invisible: the capital scores as one fortified
   * province like any other, the AI correctly reads it as an expensive target
   * with an ordinary prize, and goes and eats somebody softer instead. Sixty
   * turns can pass without a single host coming near it — which quietly deletes
   * the entire defensive half of the game for anyone who does not go looking
   * for a fight.
   *
   * With it, a settlement that grows into a real city becomes the fattest prize
   * on the map, and the answer to "why is everyone suddenly marching at me" is
   * the thing the player spent all evening building.
   */
  /**
   * And a capital is a capital.
   *
   * Without this the seats of the realm are scored as ordinary ground that
   * happens to be well defended, so every nation rationally avoids them and the
   * campaign becomes a race to eat empty moors. Nobody ever marches on anybody
   * that matters, and — since the player holds exactly one province and it is a
   * capital — nobody ever marches on the player either.
   *
   * Applied to every nation's seat, not the player's: this is not the AI being
   * told to pick on you, it is the AI being told that capitals are what wars
   * are about.
   */
  const seat = isCapital(p) ? CAPITAL_PRIZE : 1;
  /**
   * And the tier, for exactly the same reason as `development` above.
   *
   * A crown that has spent forty months building a frontier holding into a
   * great city has built the fattest prize in the region, and if the AI cannot
   * see the ladder then the safest thing on the board is a tenth-tier province
   * — which would make development a way of *hiding* from the war rather than a
   * stake in it. A city with a yard in it is what a war should be about.
   */
  return base * c.provinces[p].development * tierYield(tierOf(c, p)) * seat;
}

/** How much more attractive a nation's seat is than ordinary ground. */
const CAPITAL_PRIZE = 2.4;

const CAPITALS = new Set<number>(NATIONS.map((_, i) => nationCapital(i)));

function isCapital(p: number): boolean {
  return CAPITALS.has(p);
}

// ── Fallback ───────────────────────────────────────────────────────────────

/**
 * Nothing adjacent worth taking. March on something that is.
 *
 * `attack` only ever looks at the province next door, which is correct for
 * deciding a fight but useless for deciding a *war*: an army with no soft
 * neighbour would shuffle back to its own border and sit there. The result was
 * a realm where every nation ate whatever happened to be beside it and then
 * stopped, and where a player who kept to themselves was never visited at all —
 * sixty turns without a single host coming near the capital, which quietly
 * deleted half the game.
 *
 * So a host with nothing to do now picks the best prize *anywhere it can reach*
 * and walks toward it, one province a turn. The scoring is the same
 * `provinceValue` over `targetDefence` the adjacent case uses, discounted by
 * how far away it is, so a fat capital eight provinces off is worth crossing
 * the map for and a poor one next door is not. `attack` takes over the moment
 * the army arrives beside it.
 */
/**
 * What margin this host insists on before setting out.
 *
 * `caution` belongs here and was missing, which was the original fault: `attack`
 * made a cautious nation demand most of two to one for the province next door,
 * then execution fell through to a flat 0.9 for everybody, so the most cautious
 * power on the board declined every cheap fight and marched on the expensive
 * ones anyway.
 *
 * Simply adding `caution` here is the fix everyone reaches for, and it fails:
 * every nation turns uniformly more timid and nobody ever comes for an idle
 * player, which `ai.ts` warns about in its own comments and which
 * `tests/homefront.test.ts` catches. Two people hit that wall before this.
 *
 * Impatience is the way through. A careful nation stays careful while it has
 * something to do; a host left standing turn after turn lowers its bar until it
 * finds something. Reluctance that never decays is indistinguishable from
 * paralysis, and paralysis is what was breaking the fairness test.
 */
function marchMargin(caution: number, idleTurns: number): number {
  const wanted = 0.9 + caution * 0.75;
  const impatience = Math.max(IMPATIENCE_FLOOR, 1 - idleTurns * IMPATIENCE_PER_TURN);
  return wanted * impatience;
}

function campaignFor(
  c: CampaignState,
  n: number,
  a: Army,
  caution: number,
  holdings: number[],
): boolean {
  if (a.stance === ArmyStance.Besieging) {
    a.objective = -1;
    return false;
  }
  const hops = passageHops(c, n, a.at);
  const mine = armyPoints(a);

  // A host that has already set out keeps going. Dropping the objective only
  // when it is reached, taken by somebody else, or grown too strong to face is
  // what stops an army pacing between two provinces for a whole campaign.
  if (a.objective >= 0) {
    const keep =
      a.objective !== a.at &&
      hops[a.objective] > 0 &&
      mayAttack(c, n, a.objective) &&
      mine >= targetDefence(c, n, a.objective) * 0.75;
    if (keep) {
      const step = stepToward(c, n, a.at, a.objective);
      if (step >= 0 && canMarch(c, a, step)) {
        orderMarch(c, a, step);
        return true;
      }
    }
    a.objective = -1;
  }

  let target = -1;
  let bestValue = 0;
  for (let p = 0; p < c.provinces.length; p++) {
    if (hops[p] <= 0) continue; // unreachable, or where we already stand
    if (!mayAttack(c, n, p)) continue;
    const cost = targetDefence(c, n, p);
    // Only set out for something this host could plausibly take when it gets
    // there. Marching six provinces to lose is worse than staying home.
    if (mine < cost * marchMargin(caution, a.idleTurns)) continue;
    const value =
      (provinceValue(c, p, n) * 40 * leaderBias(holdings, n, c.provinces[p].owner)) /
      (Math.max(1, cost) * (1 + hops[p] * 0.55));
    if (value > bestValue) {
      bestValue = value;
      target = p;
    }
  }
  if (target < 0) return false;
  const next = stepToward(c, n, a.at, target);
  if (next < 0 || !canMarch(c, a, next)) return false;
  a.objective = target;
  a.idleTurns = 0;
  orderMarch(c, a, next);
  // Only announce the war, not every footstep of it.
  if (hops[target] > 1) logLine(c, `${a.name} sets out for ${provinceName(target)}.`);
  return true;
}

/** Nothing to do at all: drift toward the border, so next turn has options. */
function consolidate(c: CampaignState, n: number, a: Army): void {
  if (a.stance === ArmyStance.Besieging) return;
  const hops = passageHops(c, n, a.at);
  let target = -1;
  let bestHops = Infinity;
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== n) continue;
    if (!neighbours(c, p).some((q) => c.provinces[q].owner !== n)) continue;
    if (hops[p] < 0 || hops[p] >= bestHops) continue;
    bestHops = hops[p];
    target = p;
  }
  if (target < 0 || target === a.at) return;
  const next = stepToward(c, n, a.at, target);
  if (next >= 0 && canMarch(c, a, next)) orderMarch(c, a, next);
}

/**
 * One hop toward `to`, over ground this crown is actually allowed to walk on.
 *
 * This was a plain breadth-first hop count over the road graph, and the day
 * borders started meaning something it became a pathfinder that plans routes
 * the simulation will refuse. The failure is silent and total: `campaignFor`
 * picks the fattest prize it can *reach by road*, asks for the first step, and
 * `canMarch` says no because the step is onto a crown it is at peace with. The
 * host does nothing. Next month it does nothing again, having recomputed the
 * same unreachable objective.
 *
 * So every question the council asks about distance now goes through
 * `diplopass.ts`, which walks only provinces `mayEnter` allows. Two consequences
 * are worth stating because they are the feature working rather than the feature
 * misfiring: a crown at war with somebody behind a neutral country genuinely
 * *cannot get at them*, and will send a herald asking for the road instead —
 * that is `considerPassage` in `diploturn.ts`. And a coast that has become
 * unreachable on foot is now scored as unreachable by `surveyFrom`, which is
 * what turns a closed border into a reason to build ships.
 */
function stepToward(c: CampaignState, nation: number, from: number, to: number): number {
  return passageStep(c, nation, from, to);
}

/** Exposed for the world-map panel: how big a nation's field force is. */
export function fieldStrength(c: CampaignState, n: number): number {
  let total = 0;
  for (const a of armiesOf(c, n)) total += rawPoints(a);
  return total;
}
